import { Component } from '@theme/component';

/**
 * Sticky add-to-cart bar (sections/bondi-sticky-atc.liquid).
 *
 * - Shows once the main add-to-cart button has scrolled above the viewport.
 * - On submit it copies the chosen variant into the main product form and presses the
 *   main button, so Horizon's product form does the add, loading state and cart drawer.
 *   If there is no main form, the bar's own form posts to the cart normally.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} variantId
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} [price]
 * @property {HTMLSelectElement[]} [options]
 * @property {HTMLScriptElement} [variants]
 *
 * @extends {Component<Refs>}
 */
class BondiStickyAtc extends Component {
  requiredRefs = ['form', 'variantId', 'button'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {Array<{id: number, options: string[], available: boolean, price: string}>} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.refs.form.addEventListener('submit', this.#handleSubmit, { signal });
    for (const select of this.refs.options ?? []) {
      select.addEventListener('change', this.#handleOptionChange, { signal });
    }

    try {
      this.#variants = JSON.parse(this.refs.variants?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.#observeMainButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#observer?.disconnect();
  }

  /** @returns {HTMLButtonElement | null} */
  #mainButton() {
    const selector = this.dataset.mainButtonSelector;
    if (selector) {
      try {
        const custom = document.querySelector(selector);
        if (custom instanceof HTMLButtonElement && !this.contains(custom)) return custom;
      } catch {
        // Invalid selector in the theme editor: fall back to the product form.
      }
    }
    const fallback = document.querySelector('product-form-component button[name="add"]');
    return fallback instanceof HTMLButtonElement ? fallback : null;
  }

  #observeMainButton() {
    const target = this.#mainButton();
    if (!target) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      // Visible only once the main button is above the viewport, not while it's still below.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', scrolledPast);
    });
    this.#observer.observe(target);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const mainButton = this.#mainButton();
    const mainForm = mainButton?.form;
    const mainId = mainForm?.querySelector('input[name="id"]');
    if (!mainButton || !(mainId instanceof HTMLInputElement)) return; // let the form post natively

    event.preventDefault();
    mainId.value = this.refs.variantId.value;
    mainButton.click();
  };

  #handleOptionChange = () => {
    const selected = (this.refs.options ?? []).map((select) => select.value);
    const variant = this.#variants.find((candidate) =>
      candidate.options.every((value, index) => value === selected[index])
    );
    if (!variant) return;

    const { variantId, button, price } = this.refs;
    variantId.value = String(variant.id);
    button.disabled = !variant.available;
    button.textContent = (variant.available ? this.dataset.addText : this.dataset.soldOutText) ?? '';
    if (price) price.textContent = variant.price;
  };
}

if (!customElements.get('bondi-sticky-atc')) {
  customElements.define('bondi-sticky-atc', BondiStickyAtc);
}
